import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  CircularProgress,
  Chip,
  Paper,
  InputAdornment,
  IconButton,
  Alert
} from '@mui/material';
import {
  Timeline as TimelineIcon,
  Search as SearchIcon,
  Devices,
  AccessTime
} from '@mui/icons-material';

const API_BASE_URL =  import.meta.env.VITE_API_BASE_URL || 'https://api.bridgex.global';

// Generate consistent colors for different topics/devices
const getTopicColor = (topic) => {
  const colors = [
    '#7c3aed', // purple
    '#2563eb', // blue
    '#059669', // green
    '#dc2626', // red
    '#ea580c', // orange
    '#ca8a04', // yellow
    '#ec4899', // pink
    '#06b6d4', // cyan
    '#8b5cf6', // violet
    '#10b981', // emerald
  ];
  
  // Generate a consistent index based on the topic string
  let hash = 0;
  for (let i = 0; i < topic.length; i++) {
    hash = topic.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % colors.length;
  return colors[index];
};

const TagTimeline = () => {
  const [tagId, setTagId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [timelineData, setTimelineData] = useState([]);
  const [error, setError] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async () => {
    if (!tagId.trim()) {
      setError('Please enter a Tag ID');
      return;
    }
    
    setIsLoading(true);
    setError(null);
    setHasSearched(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/mqtt/tag-history?tag_id=${encodeURIComponent(tagId.trim())}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: Failed to fetch tag history`);
      }
      
      const result = await response.json();
      
      if (result.success && Array.isArray(result.data)) {
        // Sort by createdAt descending (newest first)
        const sortedData = [...result.data].sort((a, b) => 
          new Date(b.createdAt) - new Date(a.createdAt)
        );
        setTimelineData(sortedData);
      } else {
        setTimelineData([]);
        setError('No data found for this tag');
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch tag history');
      setTimelineData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      }),
      time: date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      })
    };
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ 
          fontWeight: 700, 
          color: '#1e293b',
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <TimelineIcon sx={{ fontSize: 40, color: '#7c3aed' }} />
          Tag Timeline
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
          Search for RFID tags and view their complete timeline history
        </Typography>
      </Box>

      {/* Search Input */}
      <Box sx={{ mb: 4 }}>
        <TextField
          label="Enter RFID Tag ID"
          placeholder="Enter tag ID..."
          variant="outlined"
          fullWidth
          value={tagId}
          onChange={(e) => setTagId(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isLoading}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  onClick={handleSearch}
                  disabled={isLoading || !tagId.trim()}
                  edge="end"
                  sx={{
                    color: '#7c3aed',
                    '&:hover': {
                      backgroundColor: 'rgba(124, 58, 237, 0.08)',
                    },
                  }}
                >
                  {isLoading ? <CircularProgress size={24} /> : <SearchIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: 'white',
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#7c3aed',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#7c3aed',
                borderWidth: 2,
              },
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: '#7c3aed',
            },
          }}
        />
      </Box>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 4 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          py: 8,
          gap: 2
        }}>
          <CircularProgress size={60} sx={{ color: '#7c3aed' }} />
          <Typography variant="h6" color="text.secondary">
            Searching timeline data...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait while we fetch the tag history
          </Typography>
        </Box>
      )}

      {/* Timeline Display */}
      {!isLoading && hasSearched && timelineData.length > 0 && (
        <Box sx={{ position: 'relative' }}>
          {/* Timeline Line */}
          <Box
            sx={{
              position: 'absolute',
              left: 40,
              top: 0,
              bottom: 0,
              width: '3px',
              backgroundColor: '#e2e8f0',
              zIndex: 1
            }}
          />

          {/* Timeline Items */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {timelineData.map((item, index) => {
              const { date, time } = formatDateTime(item.createdAt);
              const topicColor = getTopicColor(item.topic);
              
              return (
                <Box
                  key={item._id}
                  sx={{
                    position: 'relative',
                    pl: 8,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1
                  }}
                >
                  {/* Timeline Dot */}
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 32,
                      top: 8,
                      width: 18,
                      height: 18,
                      backgroundColor: topicColor,
                      borderRadius: '50%',
                      border: '4px solid white',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                      zIndex: 2
                    }}
                  />

                  {/* Content Card */}
                  <Paper
                    elevation={2}
                    sx={{
                      p: 3,
                      backgroundColor: 'white',
                      borderRadius: 2,
                      border: '1px solid #e2e8f0',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        transform: 'translateX(4px)'
                      }
                    }}
                  >
                    {/* Header with Time */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccessTime sx={{ fontSize: 20, color: '#64748b' }} />
                        <Typography variant="body1" sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {date} at {time}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`#${index + 1}`}
                        size="small"
                        sx={{ 
                          backgroundColor: '#f1f5f9',
                          color: '#64748b',
                          fontWeight: 600
                        }}
                      />
                    </Box>

                    {/* Device/Topic Info */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Devices sx={{ fontSize: 20, color: topicColor }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                            Device ID
                          </Typography>
                          <Chip 
                            label={item.topic}
                            size="small"
                            sx={{ 
                              backgroundColor: `${topicColor}15`,
                              color: topicColor,
                              fontWeight: 600,
                              borderRadius: 1,
                              height: 28,
                              '& .MuiChip-label': {
                                px: 1.5
                              }
                            }}
                          />
                        </Box>
                      </Box>

                      {/* Timewindow */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 4.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Timewindow:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#1e293b' }}>
                          {item.timewindow}
                        </Typography>
                      </Box>

                      {/* Tag ID */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pl: 4.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          Tag ID:
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontFamily: 'monospace',
                            fontWeight: 500,
                            color: '#1e293b',
                            backgroundColor: '#f8fafc',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            border: '1px solid #e2e8f0'
                          }}
                        >
                          {item.tag_id}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Empty State - No Results */}
      {!isLoading && hasSearched && timelineData.length === 0 && !error && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          backgroundColor: '#f8fafc',
          borderRadius: 2,
          border: '1px solid #e2e8f0'
        }}>
          <TimelineIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No timeline data found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            No events found for tag &quot;{tagId}&quot;. Try a different tag ID.
          </Typography>
        </Box>
      )}

      {/* Initial State */}
      {!hasSearched && !isLoading && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 8,
          backgroundColor: '#f8fafc',
          borderRadius: 2,
          border: '1px solid #e2e8f0'
        }}>
          <TimelineIcon sx={{ fontSize: 80, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Search for a Tag
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter an RFID tag ID above to view its complete timeline history
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default TagTimeline;
